import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Flag, Loader2 } from "lucide-react";

const REASONS = [
  { value: "spam", label: "Spam or scam" },
  { value: "abuse", label: "Harassment or hate speech" },
  { value: "copyright", label: "Copyright infringement" },
  { value: "misleading", label: "Misleading or false information" },
  { value: "nsfw", label: "Sexual or graphic content" },
  { value: "other", label: "Something else" },
];

/**
 * Reusable abuse-report dialog. Pass `targetType` ("product" | "comment"),
 * `targetId`, and an optional `label` for the dialog title. The report lands
 * on the backend `/api/reports` table for moderator review.
 */
export default function ReportDialog({ open, onOpenChange, targetType, targetId, label }) {
  const [reason, setReason] = useState("spam");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/reports", {
        target_type: targetType,
        target_id: targetId,
        reason,
        notes: notes.trim() || undefined,
      });
      toast.success("Report sent — thanks for keeping NoteLosh clean");
      setNotes("");
      setReason("spam");
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send report");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent
        data-testid="report-dialog"
        className="bg-[#121212] border-[#27272A] text-[#F3F4F6] sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Flag className="h-4 w-4 text-[#EF4444]" />
            Report {targetType === "product" ? "listing" : "comment"}
          </DialogTitle>
          <DialogDescription className="text-[#9CA3AF]">
            {label ? <span className="block text-xs mb-1 text-[#6B7280]">{label}</span> : null}
            Tell us what&apos;s wrong — our team reviews every report.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[11px] uppercase tracking-wider text-[#6B7280]">Reason</Label>
            <div className="grid grid-cols-1 gap-1.5" data-testid="report-reasons">
              {REASONS.map((r) => (
                <label
                  key={r.value}
                  className={`flex items-center gap-2 rounded-xl border px-3 py-2 cursor-pointer text-sm transition-colors ${
                    reason === r.value
                      ? "border-[#EF4444] bg-[#EF4444]/10 text-[#F3F4F6]"
                      : "border-[#27272A] text-[#9CA3AF] hover:text-[#F3F4F6] hover:border-[#3F3F46]"
                  }`}
                >
                  <input
                    type="radio"
                    name="report-reason"
                    value={r.value}
                    data-testid={`report-reason-${r.value}`}
                    checked={reason === r.value}
                    onChange={() => setReason(r.value)}
                    className="accent-[#EF4444]"
                  />
                  {r.label}
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="report-notes" className="text-[11px] uppercase tracking-wider text-[#6B7280]">
              Extra context <span className="normal-case text-[10px] text-[#6B7280]">(optional)</span>
            </Label>
            <Textarea
              id="report-notes"
              data-testid="report-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything that will help a moderator…"
              maxLength={500}
              rows={3}
              className="bg-[#0A0A0A] border-[#27272A] text-[#F3F4F6] placeholder:text-[#6B7280] focus-visible:ring-[#EF4444]"
            />
            <div className="text-[10px] text-[#6B7280] text-right">{notes.length}/500</div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-[#1E1E1E]"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="report-submit-btn"
            className="bg-[#EF4444] hover:bg-[#DC2626] text-white"
          >
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</> : "Send report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
